import { useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-ink border border-line px-4 py-3 text-sm text-bone placeholder:text-faint focus:outline-none focus:border-blaze transition-colors duration-200";

export default function ResetPasswordPage() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) {
      setError(t.auth.passwordMismatch);
      return;
    }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success(t.auth.resetSuccess);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t.auth.resetInvalid);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-5" data-testid="reset-password-page">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md border border-line bg-surface p-8 sm:p-10"
      >
        <img src="/logo.png" alt="Dust Republic" className="h-12 w-auto mb-8" />

        {done ? (
          <div data-testid="reset-success-alert" className="text-center py-4">
            <CheckCircle2 size={40} className="text-[#7FB383] mx-auto mb-5" />
            <h1 className="font-display text-3xl font-extrabold uppercase text-bone">{t.auth.resetSuccess}</h1>
            <Link
              to="/"
              data-testid="reset-back-home-link"
              className="mt-7 inline-block font-telemetry text-xs uppercase tracking-[0.2em] bg-blaze text-ink px-7 py-3.5 hover:bg-sand transition-colors"
            >
              {t.member.back}
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-2">
              <KeyRound size={18} className="text-blaze" />
              <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-blaze">Dust Republic — Members</p>
            </div>
            <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight text-bone mb-2">
              {t.auth.resetTitle}
            </h1>
            <p className="text-xs text-ash mb-7 leading-relaxed">{t.auth.resetSub}</p>

            <form onSubmit={submit} className="flex flex-col gap-4" data-testid="reset-password-form">
              <input
                data-testid="reset-input-password"
                type="password"
                required
                minLength={6}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder={t.auth.password}
                className={inputCls}
              />
              <input
                data-testid="reset-input-confirm"
                type="password"
                required
                minLength={6}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                placeholder={t.auth.confirmPassword}
                className={inputCls}
              />
              {error && (
                <p data-testid="reset-error-alert" className="text-xs text-[#E86A50] border border-[#D94235]/40 bg-[#D94235]/10 px-3 py-2.5">
                  {error}
                </p>
              )}
              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={busy || !token}
                className="font-telemetry text-xs uppercase tracking-[0.22em] bg-blaze text-ink px-6 py-3.5 hover:bg-sand transition-colors duration-300 disabled:opacity-60"
              >
                {t.auth.resetCta}
              </button>
            </form>

            <Link
              to="/"
              data-testid="reset-back-home-link"
              className="mt-6 flex items-center gap-2 font-telemetry text-[10px] uppercase tracking-[0.2em] text-sandmut hover:text-blaze transition-colors w-max"
            >
              <ArrowLeft size={13} />
              {t.member.back}
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
